import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type DiabetesBehandlingsform = {
    id: number;
    namn: string;
    ordning: number;
};

export type DiabetesBehandlingsformDisplay = {
    value: DiabetesBehandlingsform["id"];
    label: string;
};

type State = BaseState & {
    diabetesBehandlingsform: Map<DiabetesBehandlingsform["id"], DiabetesBehandlingsform>;
};

export const convertDiabetesBehandlingsformData: (args: {
    diabetesBehandlingsform?: DiabetesBehandlingsform;
}) => DiabetesBehandlingsformDisplay | null = ({ diabetesBehandlingsform }) => {
    if (!diabetesBehandlingsform) {
        return null;
    }

    return {
        value: diabetesBehandlingsform.id,
        label: diabetesBehandlingsform.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        diabetesBehandlingsform: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "diabetesBehandlingsform";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        diabetesBehandlingsform: (state: State) => [...state.diabetesBehandlingsform.values()],
        diabetesBehandlingsformSubset:
            (state: State) =>
            (
                props: SubsetGetterProps<State> & {
                    displayMode?: boolean;
                }
            ) => {
                const result = subsetGetter<State>({
                    ...props,
                    state,
                    limit: 0,
                    collectionName: moduleName,
                });

                if (props.displayMode && Array.isArray(result)) {
                    return result.map((singleResult) =>
                        convertDiabetesBehandlingsformData({ diabetesBehandlingsform: singleResult })
                    );
                }

                return result;
            },
        diabetesBehandlingsformById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.diabetesBehandlingsform.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addDiabetesBehandlingsform: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                diabetesBehandlingsform = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                diabetesBehandlingsform: DiabetesBehandlingsform[];
            }
        ) =>
            subsetSetter<State, DiabetesBehandlingsform>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: diabetesBehandlingsform,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchDiabetesBehandlingsform: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, DiabetesBehandlingsform>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/diabetesbehandlingsform/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addDiabetesBehandlingsform", {
                    subsetName,
                    offset: resultOffset,
                    diabetesBehandlingsform: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) =>
                              convertDiabetesBehandlingsformData({ diabetesBehandlingsform: singleResult })
                          )
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
